/**
 * The turn loop.
 *
 * The order of a turn is game design, not plumbing, so it lives in one file short
 * enough to read in a sitting. Later phases insert their steps here — food and
 * growth, production, treasury, research, culture, unrest, diplomacy decay — and the
 * order they appear in this file is the order they happen in the game.
 *
 * ITERATION ORDER IS LOAD-BEARING. Civs and units are walked in ascending id order,
 * never in whatever order a Record happens to yield. A turn engine that iterates a
 * hash map is one that eventually produces two different games from one seed, and the
 * failure shows up two hundred turns later in a save nobody can reproduce.
 */

import { isBesieged } from '../cities/siege.js';
import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { NONE, type City, type GameState, type Id, type Unit } from '../state/types.js';
import { assignWorkedTiles, cityAt, cityIndex, citiesOf, foodSurplus, growthThreshold } from '../cities/index.js';
import { tileIndex } from '../state/grid.js';
import {
  addBuilding,
  buildingsOf,
  growthFoodKept,
  heldForPopulation,
  isAtSizeCap,
  itemCost,
  itemName,
} from '../cities/production.js';
import { budget, cheapestSellableBuilding, effectiveYield, inDisorder, type Budget } from '../cities/economy.js';
import { advanceResearch } from '../research/index.js';
import { spawnUnit } from '../units/index.js';
import { indexToOffset } from '../state/grid.js';
import { allUnits, maxHp, maxMovesOf } from '../units/index.js';
import { advanceEscort, advanceGoto } from '../units/move.js';
import { advanceWork } from '../units/work.js';
import { recomputeVision } from '../vision/index.js';
import { automateWorker, takeAiTurn } from '../ai/index.js';
import { advanceCulture } from '../culture/index.js';
import { advanceGovernment } from '../government/index.js';
import { checkVictory, recordHistory } from '../score.js';
import { expelTrespassers, noteContacts } from '../diplomacy/borders.js';
import { expireOffers, processGrants, processPayments } from '../diplomacy/deals.js';
import { resourceBlocker } from '../resources/access.js';
import { pruneMemories, recoverReputation } from '../diplomacy/memory.js';
import { settleBorders } from '../cities/index.js';
import { civTrainsVeterans, purgeWonder, wonderHolder } from '../cities/wonders.js';

/** Every living civilisation's cities, in turn order. */
function citiesInOrder(state: GameState): City[] {
  return civOrder(state)
    .filter((civId) => !state.civs[civId]?.defeated)
    .flatMap((civId) => citiesOf(state, civId));
}

/**
 * Let any governed city that is going hungry choose its tiles again. A city chooses
 * around the tiles its neighbours already work; a neighbour that chooses after it may
 * let one of those go, so the first choice can leave it starving beside food it could
 * now reach (ADR-0069).
 */
function regovernHungry(state: GameState, content: Content): void {
  for (const city of citiesInOrder(state)) {
    if (city.governed && foodSurplus(state, content, city) < 0) assignWorkedTiles(state, content, city);
  }
}

/** Every city chooses its tiles, then the hungry ones choose again. */
function governCities(state: GameState, content: Content): void {
  for (const city of citiesInOrder(state)) assignWorkedTiles(state, content, city);
  regovernHungry(state, content);
}

/**
 * One city's turn: work the best tiles available, eat, and grow or starve.
 *
 * Tiles are reassigned every turn rather than only on growth, because the ground can
 * change underneath a city — a rival's borders arrive, a neighbour claims a tile —
 * and a governor that only ran on growth would leave citizens working tiles the city
 * no longer owns.
 */
function growCity(state: GameState, content: Content, city: City): GameEvent[] {
  const events: GameEvent[] = [];

  const surplus = foodSurplus(state, content, city);
  // A besieged city eats from its store but gathers nothing in (ADR-0072).
  city.foodStore += surplus > 0 && isBesieged(state, content, city) ? 0 : surplus;

  const threshold = growthThreshold(content, city.population);
  if (city.foodStore >= threshold && isAtSizeCap(content, city)) {
    // Full granaries and nowhere to put anyone. The store stays brimming so the panel
    // can say plainly that the city is waiting on an aqueduct rather than on food.
    city.foodStore = threshold;
  } else if (city.foodStore >= threshold) {
    // A granary keeps part of the box, so the next citizen does not start from nothing.
    const kept = Math.floor(threshold * growthFoodKept(content, city));
    city.foodStore = city.foodStore - threshold + kept;
    city.population++;
    assignWorkedTiles(state, content, city);
    events.push({ t: 'CityGrew', cityId: city.id, population: city.population });
  } else if (city.foodStore < 0) {
    // A city never starves out of existence in Phase 4a; it shrinks and stalls at one.
    if (city.population > 1) {
      city.population--;
      assignWorkedTiles(state, content, city);
      events.push({ t: 'CityStarved', cityId: city.id, population: city.population });
    }
    city.foodStore = 0;
  }

  return events;
}

/**
 * One city's building for the turn.
 *
 * Production carries over rather than being lost, so a city that overshoots a cheap
 * item by twenty shields starts the next one twenty shields along. Losing it is the
 * kind of quiet waste players never see and always feel.
 */
function buildInCity(state: GameState, content: Content, city: City, production: number): GameEvent[] {
  const events: GameEvent[] = [];
  city.productionStore += production;

  // A loop, because a cheap item can complete and leave enough behind for the next.
  while (city.queue.length > 0) {
    const item = city.queue[0]!;

    if (heldForPopulation(content, city, item)) {
      // A settler cannot take the last citizen out of a city. Production keeps
      // accumulating; the city simply waits until it is large enough.
      events.push({ t: 'ProductionHeld', cityId: city.id, item: itemName(content, item) });
      break;
    }

    // Someone else finished this wonder first. Normally it was already taken out of
    // the queue when they did (purgeWonder); this is the guard if it was not.
    if (item.kind === 'building' && content.buildings.get(item.building).wonder && wonderHolder(state, item.building)) {
      city.queue.shift();
      events.push({ t: 'WonderLost', cityId: city.id, name: itemName(content, item), by: NONE, stored: city.productionStore });
      continue;
    }

    const cost = itemCost(content, item, state.civs[city.owner]);
    if (city.productionStore < cost) break;

    // A unit that needs a strategic resource the city has lost waits, its production
    // kept, until the resource is back or the player builds something else (ADR-0063).
    const missing = resourceBlocker(state, content, city, item);
    if (missing !== -1) {
      events.push({ t: 'ProductionNeedsResource', cityId: city.id, item: itemName(content, item), resource: content.resources.get(missing).name });
      break;
    }

    city.productionStore -= cost;
    city.queue.shift();

    if (item.kind === 'building') {
      addBuilding(city, item.building);
      if (content.buildings.get(item.building).wonder) {
        events.push({
          t: 'WonderCompleted',
          cityId: city.id,
          civId: city.owner,
          building: item.building,
          name: itemName(content, item),
          cityName: city.name,
        });
        events.push(...purgeWonder(state, content, item.building, city));
      } else {
        events.push({ t: 'BuildingCompleted', cityId: city.id, building: item.building, name: itemName(content, item) });
      }
    } else {
      const { col, row } = indexToOffset(state.map, cityIndex(state.map, city));
      const def = content.units.get(item.type);
      // A barracks trains veterans (ADR-0050).
      const rank =
        buildingsOf(content, city).some((b) => b.effects.veteranUnits === true) || civTrainsVeterans(state, content, city.owner) ? 1 : 0;
      spawnUnit(state, content, city.owner, def.key, col, row, rank);
      if (def.populationCost > 0) {
        city.population -= def.populationCost;
        assignWorkedTiles(state, content, city);
      }
      events.push({ t: 'UnitCompleted', cityId: city.id, unitType: item.type, name: def.name });
    }
  }

  return events;
}

/**
 * Pay the bills.
 *
 * A civilisation that cannot meet its upkeep sells a building rather than carrying a
 * negative treasury. Debt with no consequence is not a constraint, and a number that
 * goes below zero and stays there is the kind of thing a player stops reading.
 */
function settleAccounts(state: GameState, content: Content, civId: Id, accounts: Budget): GameEvent[] {
  const events: GameEvent[] = [];
  const civ = state.civs[civId];
  if (!civ) return events;

  civ.treasury += accounts.net;

  while (civ.treasury < 0) {
    const sale = cheapestSellableBuilding(state, content, civId);
    if (!sale) {
      // Nothing left to sell. The treasury floors at zero rather than going negative:
      // there is no debt system, and pretending otherwise would be a lie in the panel.
      civ.treasury = 0;
      events.push({ t: 'Bankrupt', civId });
      break;
    }

    const def = content.buildings.get(sale.building);
    sale.city.buildings = sale.city.buildings.filter((id) => id !== sale.building);
    civ.treasury += Math.floor(def.cost * content.rules.economy.sellBuildingRefund);
    events.push({ t: 'BuildingSold', cityId: sale.city.id, name: def.name, cityName: sale.city.name });

    // Selling is not guaranteed to fix the deficit, so this loop keeps going — but a
    // sale always removes upkeep, so it terminates.
    if (civ.treasury < 0 && !cheapestSellableBuilding(state, content, civId)) {
      civ.treasury = 0;
      events.push({ t: 'Bankrupt', civId });
      break;
    }
  }

  return events;
}

/**
 * Recover hit points: most in one of your own cities, some in your own territory, a
 * little anywhere else. Rest is the only healing there is until medicine and barracks.
 */
function heal(state: GameState, content: Content, unit: Unit): void {
  const full = maxHp(content, unit);
  if (unit.hp >= full) return;
  const rules = content.rules.combat;
  const at = tileIndex(state.map, unit.col, unit.row);
  const city = cityAt(state, state.map, at);
  const amount =
    city && city.owner === unit.owner
      ? isBesieged(state, content, city)
        ? 0
        : rules.healCity
      : state.map.owner[at] === unit.owner
        ? rules.healOwnTerritory
        : rules.healField;
  unit.hp = Math.min(full, unit.hp + amount);
}

/** Civ ids in seat order. */
export function civOrder(state: GameState): Id[] {
  return Object.keys(state.civs)
    .map(Number)
    .sort((a, b) => a - b);
}

/**
 * Close out the active civilisation's turn.
 *
 * Phase 3 has nothing to settle here — no cities to feed, no treasury to charge, no
 * AI civs to move. The step exists so that when those arrive they have an obvious
 * home rather than being wedged into whichever function looked convenient.
 */
export function runTurnEnd(state: GameState, content: Content, civId: Id): GameEvent[] {
  const events: GameEvent[] = [{ t: 'TurnEnded', turn: state.meta.turn, civId }];

  // Every civilisation that is not the human's takes its turn now, in seat order.
  // They issue the same commands the player does, through the same validation — the
  // AI has no privileged access to anything (see src/core/ai).
  for (const other of civOrder(state)) {
    if (other === civId) continue;
    if (state.civs[other]?.isHuman || state.civs[other]?.defeated) continue;
    events.push(...takeAiTurn(state, content, other));
  }

  return events;
}

/**
 * Start the next turn: advance the clock, restore movement, let standing orders run,
 * then rebuild what everyone can see.
 *
 * Movement is restored *before* orders run, because a goto order is the unit spending
 * its new turn's movement, not a leftover from the turn that just finished.
 *
 * Vision is rebuilt once per civ at the end rather than after every step of every
 * unit's march. Each `applyMove` already refreshes the mover's own view so a scout
 * reveals ground as it walks; this pass is what catches everyone else.
 */
export function beginTurn(state: GameState, content: Content): GameEvent[] {
  const events: GameEvent[] = [];

  state.meta.turn++;
  // Offers nobody answered are withdrawn as the new turn begins (ADR-0060).
  events.push(...expireOffers(state));

  for (const unit of allUnits(state)) {
    const full = maxMovesOf(state, content, unit);
    // A unit that neither moved nor fought last turn spent it recovering.
    if (unit.movesLeft >= full) heal(state, content, unit);
    unit.movesLeft = full;
  }

  for (const unit of allUnits(state)) {
    if (unit.orders?.kind === 'goto') events.push(...advanceGoto(state, content, unit));
  }
  // Escorts left behind catch up once everything else has sailed (ADR-0067).
  for (const unit of allUnits(state)) {
    if (unit.orders?.kind === 'escort') events.push(...advanceEscort(state, content, unit));
  }

  // Work happens after movement: a worker that arrives this turn starts tomorrow,
  // which is also how the player reads it.
  events.push(...advanceWork(state, content));
  // Automated workers that have finished a job, or arrived, choose the next (ADR-0077).
  for (const unit of allUnits(state)) {
    if (unit.automated && unit.orders === null) events.push(...automateWorker(state, content, unit));
  }

  // Every city chooses its tiles before any city eats or builds. Food and production
  // are then both taken from that one snapshot: growing first and building after would
  // let a citizen born this turn add production in the turn they arrived.
  governCities(state, content);

  for (const civId of civOrder(state)) {
    if (state.civs[civId]?.defeated) continue;
    for (const city of citiesOf(state, civId)) {
      const yields = effectiveYield(state, content, city);
      if (inDisorder(state, content, city)) {
        events.push({ t: 'CityInDisorder', cityId: city.id, name: city.name });
      }
      events.push(...buildInCity(state, content, city, yields.production));
      events.push(...growCity(state, content, city));
    }
    // One set of accounts for both: the science that goes to research is exactly the
    // commerce that did not reach the treasury, computed once so they cannot disagree.
    const accounts = budget(state, content, civId);
    events.push(...settleAccounts(state, content, civId, accounts));
    // Gold owed under deals, after the books (ADR-0060).
    events.push(...processPayments(state, content, civId));
    events.push(...processGrants(state, content, civId));
    events.push(...advanceResearch(state, content, civId, accounts.science));
    events.push(...advanceCulture(state, content, civId));
    // A revolution that has run its course installs the new government; war weariness
    // fades (ADR-0059).
    events.push(...advanceGovernment(state, content, civId));
  }

  // Culture may have pushed borders out — and so pushed a neighbour's back. Ownership
  // is settled once for everybody, after every city has added its turn's culture.
  settleBorders(state, content);
  // Anyone standing where they may not — peace made, open borders ended, a border grown
  // over them — goes home (ADR-0060).
  events.push(...expelTrespassers(state, content));
  // A new government, a pillaged farm or a border moved since the cities chose may have
  // left one hungry; it chooses again before the next turn is played.
  regovernHungry(state, content);

  for (const civId of civOrder(state)) {
    const discovered = recomputeVision(state, content, civId);
    if (discovered.length > 0) events.push({ t: 'TilesDiscovered', civId, count: discovered.length });
  }
  noteContacts(state);
  pruneMemories(state, content);
  recoverReputation(state, content);
  events.push(...checkVictory(state, content));
  recordHistory(state, content);

  events.push({ t: 'TurnBegan', turn: state.meta.turn, civId: state.meta.activeCiv });
  return events;
}
